/**
 * Account selection — which IB account alias is currently scoped to.
 *
 * `activeAlias = null` means "all accounts" (aggregate view).
 *
 * Switching the alias DROPS the React Query cache. That is not an
 * optimization detail: the alias rides on the `X-Compass-Account` header, so
 * it is not part of any query key, which means every cached response was an
 * answer to a question about the previous account. Keeping any of it renders
 * one account's data under another account's name — the exact bug this
 * machinery exists to prevent.
 *
 * (This file previously claimed the switch invalidated queries. Nothing did;
 * no subscriber existed. Now it does.)
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { queryClient } from '@/lib/queryClient'

// Aliases are dynamic (defined in Settings → Brokers). The canonical list comes
// from useBrokerAccounts() at runtime; ACCOUNT_ALIASES is only a load-time
// fallback for the account selector before that query resolves.
export type AccountAlias = string
export const ACCOUNT_ALIASES: AccountAlias[] = ['paper', 'personal', 'roth', 'tnv']

export interface AccountInfo {
  alias: AccountAlias
  ib_account_id: string
  account_name: string
  connected?: boolean
}

interface AccountState {
  /** null = "all accounts" aggregate view */
  activeAlias: AccountAlias | null
  /** Resolved from the broker accounts endpoint; populated by the Header. */
  accounts: AccountInfo[]
  setActiveAlias: (alias: AccountAlias | null) => void
  setAccounts: (accounts: AccountInfo[]) => void
}

export const useAccountStore = create<AccountState>()(
  persist(
    (set, get) => ({
      activeAlias: null,
      accounts: [],
      setActiveAlias: (activeAlias) => {
        if (get().activeAlias === activeAlias) return
        set({ activeAlias })
        // removeQueries, not invalidateQueries: invalidate refetches but keeps
        // serving the stale rows meanwhile, which is a frame of the wrong
        // account's data. Remove drops them, so components show a loading
        // state until the new scope answers.
        queryClient.removeQueries()
      },
      setAccounts: (accounts) => set({ accounts }),
    }),
    {
      name: 'compass.account',
      partialize: (s) => ({ activeAlias: s.activeAlias }),
    },
  ),
)

export function useActiveAccount(): AccountInfo | null {
  const { activeAlias, accounts } = useAccountStore()
  if (!activeAlias) return null
  return accounts.find((a) => a.alias === activeAlias) ?? null
}
